import {action, computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import errorToast from "../../../components/toast/ErrorToast";
import SaporeForm from "../../../forms/SaporeForm";
import MultipleOptionsField from "../../../forms/fields/MultipleOptionField";
import successToast from "../../../components/toast/SuccessToast";
import PermissionAdapter from "../../../adapters/PermissionAdapter";
import CustomerGroupMultipleOptionField from "../../../forms/fields/CustomerGroupMultipleOptionField";
import Feature from "../../Feature";

export default class AssignPermissionsFeature extends Feature {
    constructor(application, navigator, anEmail) {
        super(application);
        this._navigator = navigator;
        this._customersGroupsOptions = [];
        this._permissionsOptions = []
        this._form = null;
        this._permissionSearchText = '';
        this._emailFromUser = anEmail;
        this._userPermissions = null
        this._userCustomersGroups = null

        makeObservable(this, {
            _form: observable,
            form: computed,
            _permissionsOptions: observable,
            permissionGroups: computed,
            _permissionSearchText: observable,
            permissionSearchText: computed,
            searchPermissions: action,
            customersGroupsOptions: computed,
            _customersGroupsOptions: observable,
            userCustomersGroups: computed,
            _userCustomersGroups: observable,
            userPermissions: computed,
            _userPermissions: observable,
            isFormValid: computed,
        })
    }

    async load() {
        await this._allPermissions()
        await this._customersGroups()
        await this.getUserWithPermissionsAndCustomerGroups()

        this._createForm()
        this.finishLoading()
    }

    async aValueChanged() {
        if (this.canEditUser()) {
            await this.editUser()
        }
    }

    selectAllCustomerGroups(){
        const allCustomerGroups = this.customersGroupsOptions
        this.form.updateFieldWithValue('customerGroups', allCustomerGroups)
    }

    _createForm() {
        const permissions = this.permissionsFieldOptions()
        const customersGroups = this.customerGroupFieldOptions()

        const initialPermissions = this._initialPermissions();
        const initialCustomersGroups = this._initialCustomersGroups();

        const fields = {
            "permissions": new MultipleOptionsField(permissions, true, this, initialPermissions),
            "customerGroups": new CustomerGroupMultipleOptionField(customersGroups, true, this, initialCustomersGroups),
        }
        this._form = new SaporeForm(fields);
    }

    permissionsFieldOptions() {
        return this._permissionsOptions.map(permission => {
            return {label: new PermissionAdapter(permission).adapt(), value: permission}
        });
    }

    get permissionSearchText() {
        return this._permissionSearchText
    }

    searchPermissions(searchedText) {
        this._permissionSearchText = searchedText
    }

    matchesPermissionSearch(permission) {
        const searchedText = this._permissionSearchText.trim().toLowerCase()
        const permissionLabel = new PermissionAdapter(permission).adapt().toLowerCase()
        return permissionLabel.includes(searchedText)
    }

    customerGroupFieldOptions() {
        return this._customersGroupsOptions.map(customerGroup => {
            return {
                label: `${customerGroup.name} - ${customerGroup.code}`,
                value: {
                    code: customerGroup.code,
                    name: customerGroup.name
                }
            }
        });
    }

    userEmail() {
        return this._emailFromUser
    }

    _initialCustomersGroups() {
        return this._userCustomersGroups.map(customerGroup => {
            return {
                code: customerGroup.code,
                name: customerGroup.name
            }
        })
    }

    _initialPermissions() {
        return this._userPermissions.map(permission => {
            return permission
        })
    }

    async _allPermissions() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._permissionsOptions = response.permissions()
            },
            handlesError: (response) => {
                errorToast('No se pudieron obtener los permisos')
            }
        });
        return this._application.apiClient().allPermissions(responseHandler)
    }

    async _customersGroups() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._customersGroupsOptions = response.customersGroups()
            },
            handlesError: (response) => {
                errorToast('No se pudieron obtener los grupos de clientes')
            }
        });
        return this._application.apiClient().customerGroups(responseHandler)
    }

    async getUserWithPermissionsAndCustomerGroups() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._userPermissions = response.permissions()
                this._userCustomersGroups = response.customersGroups()
            },
            handlesError: (response) => {
                errorToast('Ha ocurrido un error al obtener el usuario.')
            }
        });
        return this._application.apiClient().userWithRolesAndPermissionsGranted(responseHandler, this.userEmail());
    }

    async editUser() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                successToast("Se ha modificado al usuario.")
            },
            handlesError: (response) => {
                errorToast("No se pudieron guardar los cambios.")
            }
        });
        const userEmail = this.userEmail()
        const customersGroups = this.selectedCustomersGroups()
        const permissions = this.selectedPermissions()
        this._application.apiClient().editUser(userEmail, permissions, customersGroups, responseHandler);
    }

    selectedCustomersGroups() {
        return this.form.getFieldValue('customerGroups')
    }

    selectedPermissions() {
        return this.form.getFieldValue('permissions')
    }

    get permissionGroups() {
        const groupedPermissionGroups = [
            this._ordersPermissionGroup(),
            this._currentAccountPermissionGroup(),
            this._authorizationsPermissionGroup(),
            this._customerOnboardingPermissionGroup(),
            this._customerOnboardingByAreaPermissionGroup(),
            this._supportPermissionGroup(),
            this._reportsPermissionGroup(),
            this._logisticsPermissionGroup(),
            this._allowancesPermissionGroup(),
            this._administrationPermissionGroup(),
        ]
        const groupedPermissions = groupedPermissionGroups.flatMap(permissionGroup => permissionGroup.permissions)
        const ungroupedPermissions = this._permissionsOptions.filter(permission => !groupedPermissions.includes(permission))
        const hasUngroupedPermissions = ungroupedPermissions.length > 0
        return hasUngroupedPermissions ? [...groupedPermissionGroups, this._permissionList('Otros', ungroupedPermissions)] : groupedPermissionGroups
    }

    _ordersPermissionGroup() {
        return this._permissionList('Pedidos', ['ORDER_PERMISSION', 'CREATE_ORDER_PERMISSION', 'PURCHASE_ORDER_PERMISSION'])
    }

    _currentAccountPermissionGroup() {
        return this._permissionList('Cuenta corriente', ['CURRENT_ACCOUNT_PERMISSION', 'PAYMENT_REMINDER_PERMISSION', 'HIGH_RISK_CLASSIFICATION_PERMISSION', 'KAM_DAILY_REPORT_PERMISSION'])
    }

    _authorizationsPermissionGroup() {
        return this._permissionList('Autorizaciones', ['AUTHORIZATION_PERMISSION', 'FREE_ORDER_CONFIRMATION_REQUEST_PERMISSION', 'ADVANCE_PAYMENT_REQUEST_PERMISSION', 'JUDICIAL_MANAGEMENT_REQUEST_PERMISSION'])
    }

    _customerOnboardingPermissionGroup() {
        return this._permissionList('Altas de clientes', ['CLOSER_PERMISSION', 'DELIVERY_SITE_ONBOARDING_PERMISSION', 'ONBOARDINGS_ERP_IMPORTER_PERMISSION'])
    }

    _customerOnboardingByAreaPermissionGroup() {
        return this._permissionMatrix('Altas de clientes por área', ['Procesa', 'Autoriza'], [
            {name: 'Comercial', permissions: ['COMMERCIAL_PERMISSION', 'COMMERCIAL_AUTHORIZATION_PERMISSION']},
            {name: 'Logística', permissions: ['LOGISTICS_PERMISSION', 'LOGISTICS_AUTHORIZATION_PERMISSION']},
            {name: 'Impuestos', permissions: ['TAX_PERMISSION', 'TAX_AUTHORIZATION_PERMISSION']},
            {name: 'Finanzas', permissions: ['FINANCE_PERMISSION', 'FINANCE_AUTHORIZATION_PERMISSION']},
        ])
    }

    _supportPermissionGroup() {
        return this._permissionList('Soporte', ['SUPPORT_PERMISSION', 'ERP_SUPPORT_PERMISSION', 'EDI_SUPPORT_PERMISSION', 'CURRENT_ACCOUNT_SUPPORT_PERMISSION', 'GENERAL_SUPPORT_PERMISSION'])
    }

    _reportsPermissionGroup() {
        return this._permissionList('Reportes', ['DASHBOARD_PERMISSION', 'SOLD_BOXES_BY_CUSTOMER_AND_ARTICLE_PERMISSION'])
    }

    _logisticsPermissionGroup() {
        return this._permissionList('Logística', ['LOGISTICS_SYSTEM_PERMISSION'])
    }

    _allowancesPermissionGroup() {
        return this._permissionList('Bonificaciones', ['ALLOWANCES_CREDIT_NOTES_PERMISSION'])
    }

    _administrationPermissionGroup() {
        return this._permissionList('Administración', ['USER_PERMISSION', 'BACKOFFICE_USER_PERMISSION', 'BUDGET_PERMISSION', 'DEMO_PERMISSION', 'SUPERUSER_PERMISSION'])
    }

    _permissionList(name, permissions) {
        return {type: 'list', name: name, permissions: permissions}
    }

    _permissionMatrix(name, columnNames, rows) {
        const permissions = rows.flatMap(row => row.permissions)
        return {type: 'matrix', name: name, columnNames: columnNames, rows: rows, permissions: permissions}
    }

    get isFormValid() {
        return this.form.isValid;
    }

    get form() {
        return this._form;
    }

    set form(newForm) {
        this._form = newForm;
    }

    canEditUser() {
        return this.form && this.customersGroupsField().hasValue && this.permissionsField().hasValue
    }

    getFieldByName(fieldName) {
        return this.form.getFieldByName(fieldName);
    }

    customersGroupsField() {
        return this.getFieldByName("customerGroups");
    }

    permissionsField() {
        return this.getFieldByName("permissions");
    }

    get customersGroupsOptions() {
        return this._customersGroupsOptions;
    }

    get userCustomersGroups() {
        return this._userCustomersGroups;
    }

    get userPermissions() {
        return this._userPermissions;
    }

}